/* eslint-disable no-console */
import { openDB } from 'idb';

// Offline read cache for the dashboard. Persists a small subset of Vuex
// modules to IndexedDB so an agent can open the app without network and
// still see their recent conversations, contacts and inboxes.
//
// Deliberately narrow: reports, search results, UI flags and any
// transient state stay in memory only. Persisting those would bloat the
// cache and produce stale results when the user comes back online.
//
// Keyed per account so a shared browser never leaks one user's
// conversations into another user's session.

const DB_NAME = 'synkra-chat-persist';
const DB_VERSION = 1;
const STORE_NAME = 'state';
const KEY_PREFIX = 'vuex-';

const PERSISTED_MODULES = [
  'accounts',
  'agents',
  'contacts',
  'conversations',
  'inboxes',
  'labels',
  'teams',
  'teamMembers',
];

let dbPromise = null;

const getDb = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
};

const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

const snapshot = state => {
  const out = {};
  PERSISTED_MODULES.forEach(name => {
    if (state[name] !== undefined) out[name] = state[name];
  });
  return out;
};

export default function persistStatePlugin(store) {
  let currentKey = null;

  const persist = debounce(async () => {
    if (!currentKey) return;
    try {
      const db = await getDb();
      await db.put(STORE_NAME, snapshot(store.state), currentKey);
    } catch (e) {
      console.warn('[persist] save failed:', e);
    }
  }, 1000);

  store.subscribe(async (mutation, state) => {
    // Learn the current account the first time it's set, then hydrate
    // from cache. Before this point we have nothing to key by.
    if (mutation.type === 'accounts/setActiveAccount') {
      const accountId = store.getters.getCurrentAccountId;
      if (!accountId) return;
      currentKey = `${KEY_PREFIX}${accountId}`;

      try {
        const db = await getDb();
        const saved = await db.get(STORE_NAME, currentKey);
        if (!saved) return;
        const merged = { ...store.state };
        Object.entries(saved).forEach(([moduleName, moduleState]) => {
          merged[moduleName] = moduleState;
        });
        store.replaceState(merged);
      } catch (e) {
        console.warn('[persist] hydrate failed:', e);
      }
      return;
    }

    // Clear cache on logout so a shared browser doesn't retain data.
    if (
      mutation.type === 'auth/clearUser' ||
      mutation.type === 'auth/logout' ||
      mutation.type === 'accounts/clearActiveAccount'
    ) {
      if (currentKey) {
        try {
          const db = await getDb();
          await db.delete(STORE_NAME, currentKey);
        } catch (e) {
          console.warn('[persist] clear failed:', e);
        }
        currentKey = null;
      }
      return;
    }

    if (!currentKey) return;
    if (mutation.type.startsWith('persist/')) return;
    persist(state);
  });
}
